import React, { useMemo, useCallback } from "react";
import NiceModal from "@ebay/nice-modal-react";
import SubscriptionPlanDTO, {
  SubscriptionPlanType
} from "@tootsweet/model/datatransfer/SubscriptionPlanDTO";
import ManagedAccount from "@tootsweet/model/entities/ManagedAccount";
import KC_Tag from "../../../designsystem/KC_Tag/KC_Tag";
import KC_Heading3 from "../../../designsystem/typography/KC-Heading/KC_Heading3";
import KC_Heading1 from "../../../designsystem/typography/KC-Heading/KC_Heading1";
import KC_Button, {
  KC_ButtonSize
} from "../../../designsystem/KC_Button/KC_Button";
import {
  DEFAULT_PLAN_OPTION,
  DURATION_BASED_DISCOUNT
} from "../../../utils/constants";
import { LANGUAGE, tt } from "../../../utils/i18n/i18n";
import TSUtils from "../../../utils/helpers/TSUtils";
import { useCurrencySubUnitsMapSelector } from "../../../redux/actions/app";
import KC_Heading2 from "../../../designsystem/typography/KC-Heading/KC_Heading2";
import KC_UIText from "../../../designsystem/typography/KC_UIText/KC_UIText";
import FreeTrialModal from "../../../app/19_JetpackConfig/components/FreeTrialModal";
import ConfirmModal from "../../modal/ConfirmModal/ConfirmModal";
import "./styles.scss";

interface PlanProps {
  plan: SubscriptionPlanDTO;
  duration: string;
  currency: string;
  isSelected: boolean;
  displayButtonOnFreePlan?: boolean;
  freePlanIsPink?: boolean;
  useSmallerFontsForPrices?: boolean;
  hasKCTheme?: boolean;
  accountToDisplay?: ManagedAccount | null;
  subscriptionPlans?: SubscriptionPlanDTO[] | null;
  onClick?: (plan: SubscriptionPlanDTO) => void;
  hideFree?: boolean;
}

const PlanOption = (props: PlanProps) => {
  const currencySubUnitsMap = useCurrencySubUnitsMapSelector();

  const promotionFactor = useMemo(() => {
    return props.duration === DEFAULT_PLAN_OPTION
      ? 1 - DURATION_BASED_DISCOUNT
      : 1;
  }, [props.duration]);

  const priceText = useMemo(() => {
    if (props.plan.noPrice) {
      return tt("plan_option_quote");
    }

    let price = Math.trunc(
      ((props.plan.priceL?.[props.currency] ?? props.plan.price) || 0) *
        promotionFactor
    );

    if (promotionFactor < 1) {
      const modulo = price % 100;
      if (modulo > 0) {
        price += 100 - modulo;
      }
    }

    // Display prices above 1 million in compact form (e.g., 1,528,000 Rp => 1,528 M Rp)
    if (price >= 1000000) {
      const priceInThousands = Math.trunc(price / 1000);
      const formatted = TSUtils.formatAmountWithLocalizedFormatter(
        priceInThousands,
        currencySubUnitsMap[props.currency],
        props.currency,
        true
      );

      // Insert M before the currency symbol (assumes format like "1,528 Rp")
      const lastSpaceIndex = formatted.lastIndexOf(" ");
      if (lastSpaceIndex > 0) {
        return (
          formatted.slice(0, lastSpaceIndex) +
          " M" +
          formatted.slice(lastSpaceIndex)
        );
      }

      return formatted + " M";
    }

    return TSUtils.formatAmountWithLocalizedFormatter(
      price,
      currencySubUnitsMap[props.currency],
      props.currency,
      true
    );
  }, [props.plan, props.currency, promotionFactor, currencySubUnitsMap]);

  const displayButton = useMemo(() => {
    if (
      props.plan.type === SubscriptionPlanType.FREE &&
      !props.displayButtonOnFreePlan
    ) {
      return false;
    }
    return true;
  }, [props]);

  const getStyles = () => {
    if (
      props.plan.type === SubscriptionPlanType.ENTERPRISE ||
      props.plan.saasFeatures?.ip
    ) {
      return {
        containerClass: `plan-container plan-club ${props.isSelected ? "plan-gold" : ""}`,
        // buttonClass: `btn d-flex w-100 btn_upsell ${props.isSelected ? "" : ""}`,
        iconClass: "check-icon check-icon-gold"
      };
    }

    if (!props.plan.price) {
      return {
        containerClass:
          "plan-container plan-club " +
          (props.isSelected ? "kc-btn-border-black" : ""),
        // buttonClass:
        // "btn d-flex w-100 btn_inverted",
        iconClass: "check-icon check-icon-club"
      };
    }

    if (props.plan.price) {
      return {
        containerClass:
          "plan-container plan-club " +
          (props.isSelected ? "kc-btn-border-black" : ""),
        // buttonClass: "btn d-flex w-100 btn_black",
        iconClass: "check-icon check-icon-club"
      };
    }

    return {
      containerClass: "",
      // buttonClass: "",
      iconClass: ""
    };
  };

  const styles = useMemo(
    () => getStyles(),
    [props.plan.type, props.freePlanIsPink]
  );

  const buttonTheme = (type: number) => {
    if (props.plan.type === SubscriptionPlanType.ENTERPRISE) {
      return {
        isPrimary: false,
        isInverted: false,
        isUpsell: true
      };
    }

    if (!props.plan.price) {
      return {
        isPrimary: false,
        isInverted: true,
        isUpsell: false,
        isBlack: false,
        isBorderedBlack: true
      };
    }

    if (props.plan.price && !props.plan.saasFeatures?.ip) {
      return {
        isPrimary: false,
        isInverted: false,
        isUpsell: false,
        isBlack: true,
        isBorderedBlack: false
      };
    }

    if (props.plan.saasFeatures?.ip) {
      return {
        isPrimary: false,
        isInverted: false,
        isUpsell: true
      };
    }

    return {
      isPrimary: true,
      isInverted: props.isSelected,
      isUpsell: false
    };
  };

  const startTextId =
    props.plan.type === SubscriptionPlanType.ENTERPRISE
      ? "plan_option_request_quote"
      : "start";

  const handleClick = useCallback(async () => {
    if (props.isSelected) {
      return;
    }

    //
    // Check if this is a downgrade from paid to free
    //
    const isFreePlan = !props.plan.price;
    const currentUserPlanId = props.accountToDisplay?.saasPlanId;

    const currentPlan =
      props.subscriptionPlans?.find((plan) => plan._id === currentUserPlanId) ??
      null;

    const isCrossUniverseMove =
      !props.plan?.saasFeatures?.ip &&
      currentPlan &&
      props.plan &&
      !!currentPlan.isLoyalty !== !!props.plan.isLoyalty;

    const isDowngrade =
      isFreePlan &&
      currentPlan &&
      ((currentPlan.price && currentPlan.price > 0) ||
        (!currentPlan?.price && !props.plan?.price));

    if (!props.hideFree && (isCrossUniverseMove || isDowngrade)) {
      //
      // User has a paid plan and is trying to select a free plan - show downgrade blocked modal
      //
      const hasPid = !!props.accountToDisplay?.businessUser?.pid;

      if (hasPid) {
        //
        // User already has a pid, show acknowledgment modal
        //
        await NiceModal.show(ConfirmModal, {
          title: tt("downgrade_blocked_title"),
          children: <p>{tt("downgrade_request_acknowledged_message")}</p>,
          hideAction: true
        });
      } else {
        //
        // User doesn't have a pid, show phone collection modal
        //
        if (!props.accountToDisplay?.token) {
          return;
        }

        const phoneNumber =
          props.accountToDisplay?.businessUser?.contactPhoneNumber || "";

        const currentPlanName =
          currentPlan.longNameL?.[LANGUAGE] ??
          currentPlan.nameL?.[LANGUAGE] ??
          currentPlan.name;
        const targetPlanName =
          props.plan.longNameL?.[LANGUAGE] ??
          props.plan.nameL?.[LANGUAGE] ??
          props.plan.name;
        const description = `downgrade from ${currentPlanName} to ${targetPlanName}`;

        const confirmed = await NiceModal.show(FreeTrialModal, {
          initialPhoneNumber: phoneNumber,
          token: props.accountToDisplay.token,
          trackingID: props.accountToDisplay.trackingID,
          description: description,
          customTitle: tt("downgrade_blocked_title"),
          customMessage: tt("downgrade_blocked_message")
        });

        if (confirmed) {
          await NiceModal.show(ConfirmModal, {
            title: tt("downgrade_contact_confirmed_title"),
            children: <p>{tt("downgrade_contact_confirmed_message")}</p>,
            hideAction: true
          });
        }
      }

      return;
    }

    props.onClick?.(props.plan);
  }, [
    props.isSelected,
    props.plan,
    props.accountToDisplay,
    props.subscriptionPlans,
    props.onClick
  ]);

  const textClsName = useMemo(() => {
    if (
      props.plan.type === SubscriptionPlanType.PREMIUM ||
      props.plan.type === SubscriptionPlanType.ENTERPRISE
    ) {
      return "text-upsell";
    }
    if (!props.plan.price) {
      return "";
    }
    return "";
  }, [props.plan.type]);

  const TAG = useMemo(() => {
    if (props.plan.saasFeatures?.ip) {
      return <KC_Tag text={tt("premium_plus")} type="upsell" />;
    }
    if (props.plan.type === SubscriptionPlanType.ENTERPRISE) {
      return <KC_Tag text={tt("enterprise")} type="upsell" />;
    }
    if (!props.plan.price) {
      return <KC_Tag text={tt("light")} type="primary-inverted" />;
    }
    return <KC_Tag text={tt("premium")} type="black" />;
  }, [props.plan]);

  const PRICE = useMemo(() => {
    if (props.plan.type === SubscriptionPlanType.ENTERPRISE) {
      return <KC_Heading2 text={tt("custom_quote")} className={textClsName} />;
    }

    if (!props.plan.price) {
      return <KC_Heading1 text={tt("free")} className={textClsName} />;
    }

    if (props.plan.price) {
      if (props.useSmallerFontsForPrices) {
        return <KC_Heading2 text={priceText} className={textClsName} />;
      }
      return <KC_Heading1 text={priceText} className={textClsName} />;
    }

    return null;
  }, [props.plan, props.useSmallerFontsForPrices, priceText, textClsName]);

  const NAME = useMemo(() => {
    if (props.plan.type === SubscriptionPlanType.ENTERPRISE) {
      return tt("custom_str");
    }
    return props.plan.nameL?.[LANGUAGE] ?? props.plan.name;
  }, [props.plan]);

  console.log(props.plan.desc, LANGUAGE);

  return (
    <div className={styles.containerClass}>
      <div>
        <div className="plan-header">
          <div
            className="d-flex justify-content-between align-items-center"
            style={{ height: 40 }}
          >
            <div>
              <KC_Heading3 text={NAME} className={textClsName} />
            </div>
            {TAG}
          </div>
          <div
            className="plan-price"
            style={{ display: "flex", flexWrap: "wrap" }}
          >
            <span className="price">{PRICE}</span>
            {!!props.plan.price && !props.plan.noPrice && (
              <span className="price-period">
                {tt("plan_option_monthly_ht")}
              </span>
            )}
          </div>
        </div>

        <div className="plan-features">
          {props.plan?.desc?.[LANGUAGE]?.map((section, index) => (
            <React.Fragment key={index}>
              <KC_UIText text={section.title} weight="bold" />
              {section.content.split("|")?.map((item, itemIndex) => (
                <React.Fragment key={itemIndex}>
                  <br />
                  <KC_UIText text={item.trim()} weight="reg" />
                </React.Fragment>
              ))}
              <br />
              <br />
            </React.Fragment>
          ))}
        </div>
      </div>
      {props.isSelected && (
        <KC_Heading3
          text={tt("plan_option_current_plan")}
          className="ml-auto mr-auto d-flex align-items-center"
          style={{ height: 48 }}
        />
      )}
      {!props.isSelected && displayButton && (
        <KC_Button
          id={`subscription-btn-${props.plan._id}`}
          size={KC_ButtonSize.Large}
          text={tt(startTextId)}
          // className={styles.buttonClass}
          {...buttonTheme(props.plan.type || SubscriptionPlanType.FREE)}
          onClick={handleClick}
        />
      )}
    </div>
  );
};

export default PlanOption;
